/**
 * Map Tab Component
 * Map view for the project workspace with element-centric inspection workflow
 */

import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { type DBProject } from '../../../db';
import { projectsApi, photosApi } from '../../../lib/api';
import { Layers, MapPin, Camera, Search, X, Eye, Sun, Moon } from 'lucide-react';
import { ElementDetailPanel } from '../ElementDetailPanel';
import { PhotoViewer } from '../../Photo/PhotoViewer';
import { useUIStore } from '../../../store/uiStore';
import { usePhotoStore } from '../../../store/photoStore';

// Fix for default markers
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface MapTabProps {
  project: DBProject;
}

// Component to center map on highlighted photo
function HighlightedPhotoHandler({ photos, highlightedPhotoId }: { photos: any[], highlightedPhotoId: string | null }) {
  const map = useMap();
  
  useEffect(() => {
    if (!highlightedPhotoId) return;
    
    const photo = photos.find(p => p.id === highlightedPhotoId);
    if (photo && photo.lat && photo.lng) {
      map.flyTo([photo.lat, photo.lng], 18, { duration: 1 });
    }
  }, [map, photos, highlightedPhotoId]);
  
  return null;
}


export function MapTab({ project }: MapTabProps) {
  const [bounds, setBounds] = useState<L.LatLngBoundsExpression | null>(null);
  const [photos, setPhotos] = useState<any[]>([]);
  const [elements, setElements] = useState<any[]>([]);
  const [trajectory, setTrajectory] = useState<Array<[number, number]>>([]);
  const [selectedElement, setSelectedElement] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'ALL' | 'SEGMENT' | 'SUPPORT' | 'STATION'>('ALL');
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [layers, setLayers] = useState({
    trajectory: true,
    stations: true,
    segments: true,
    supports: true,
    photos: true,
  });
  const [mapTheme, setMapTheme] = useState<'light' | 'dark'>('light');
  
  const { currentPhoto, setCurrentPhoto, selectElement: selectElementInStore, highlightedPhotoId } = useUIStore();
  const { getPhotoUrl, getPhotoById, loadPhotosFromServer, photos: storePhotos, currentProjectId } = usePhotoStore();

  // Load photos and elements from server
  useEffect(() => {
    const loadData = async () => {
      setIsDataLoading(true);
      try {
        // Load photos into the store (this makes them available in PhotoViewer too)
        if (currentProjectId !== project.id) {
          await loadPhotosFromServer(project.id);
        }
        
        // Also load photos for local map display (with thumbnail URLs)
        const photosRes = await projectsApi.getPhotos(project.id, { 
          withUrls: true,
          thumbnail: true,
          limit: 1000 // Get all photos for map display
        });
        setPhotos(photosRes.data?.data || []);
        
        // Load elements
        const elementsRes = await projectsApi.getElements(project.id, { 
          limit: 1000 // Get all elements
        });
        setElements(elementsRes.data?.data || []);
      } catch (error) {
        console.error('Failed to load map data:', error);
      } finally {
        setIsDataLoading(false);
      }
    };
    loadData();
  }, [project.id, currentProjectId, loadPhotosFromServer]);

  // Load trajectory from API
  useEffect(() => {
    const loadTrajectory = async () => {
      try {
        const response = await projectsApi.getTrajectory(project.id);
        const points = response.data?.data?.points || [];
        console.log('Trajectory raw points:', points.slice(0, 3)); // Debug first 3 points
        // Convert [lat, lng, alt, timestamp] to [lat, lng] for Leaflet
        const trajectoryPoints = points
          .filter((p: number[] | null) => p && p[0] != null && p[1] != null)
          .map((p: number[]) => [p[0], p[1]] as [number, number]);
        console.log('Trajectory converted:', trajectoryPoints.slice(0, 3)); // Debug
        setTrajectory(trajectoryPoints);
      } catch (error) {
        console.error('Failed to load trajectory:', error);
        setTrajectory([]);
      }
    };
    loadTrajectory();
  }, [project.id]);

  // Calculate bounds from elements, photos, and trajectory
  useEffect(() => {
    const coords: [number, number][] = [];
    
    // Add trajectory coordinates
    trajectory.forEach(point => {
      if (point && point[0] != null && point[1] != null) {
        coords.push(point);
      }
    });
    
    // Add elements coordinates
    elements.forEach(el => {
      if (el.lat && el.lng) {
        coords.push([el.lat, el.lng]);
      }
      // Also add geometry points if exists
      if (el.geometry?.coordinates) {
        if (el.geometry.type === 'LineString') {
          el.geometry.coordinates.forEach((coord: number[] | null) => {
            if (coord && coord[0] != null && coord[1] != null) {
              coords.push([coord[1], coord[0]]); // GeoJSON is [lng, lat]
            }
          });
        } else if (el.geometry.type === 'Point' && el.geometry.coordinates[0] != null && el.geometry.coordinates[1] != null) {
          coords.push([el.geometry.coordinates[1], el.geometry.coordinates[0]]);
        }
      }
    });
    
    // Add photos with GPS
    photos.forEach(p => {
      if (p.lat && p.lng) {
        coords.push([p.lat, p.lng]);
      }
    });
    
    if (coords.length > 0) {
      setBounds(L.latLngBounds(coords));
    }
  }, [elements, photos, trajectory]);

  // Filter elements based on search and type
  const filteredElements = elements.filter(el => {
    if (filterType !== 'ALL' && el.type.toUpperCase() !== filterType) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        el.code?.toLowerCase().includes(query) ||
        el.elementId?.toString().includes(query)
      );
    }
    return true;
  });

  // Handle element click
  const handleElementClick = (element: any) => {
    setSelectedElement(element);
  };

  // Handle photo click from detail panel
  const handlePhotoClick = async (photoId: string) => {
    const photo = photos.find(p => p.id === photoId);
    if (!photo) {
      console.error('Photo not found:', photoId);
      return;
    }
    
    try {
      // Select element bound to photo (priority: photo's element > selected element on map)
      const elementIdToSelect = photo.elementId || selectedElement?.id;
      const elementTypeToSelect = photo.elementType || selectedElement?.type?.toUpperCase();
      
      if (elementIdToSelect && elementTypeToSelect) {
        selectElementInStore(
          elementIdToSelect, 
          elementTypeToSelect as any
        );
      }
      
      // Get full-size photo URL
      try {
        const urlRes = await photosApi.getUrl(photoId, false);
        const photoUrl = urlRes.data?.data?.url || urlRes.data?.url;
        
        if (!photoUrl) {
          console.error('Failed to get photo URL:', photo.filename);
          return;
        }

        // Fetch blob to create File object
        const response = await fetch(photoUrl);
        const blob = await response.blob();
        const file = new File([blob], photo.filename, { type: blob.type });
        
        // Set current photo in UI store
        setCurrentPhoto({
          id: photoId,
          projectId: project.id,
          file,
          dataUrl: photoUrl,
          width: photo.width || 0,
          height: photo.height || 0,
        });
      } catch (error) {
        console.error('Failed to load photo:', error);
      }
    } catch (error) {
      console.error('Failed to load photo:', error);
    }
  };

  return (
    <div className="h-full w-full flex relative">
      {/* Left Sidebar - Elements List */}
      <div className="w-80 bg-white dark:bg-slate-800/95 border-r border-slate-200 dark:border-slate-700/50 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-700/50">
            <h3 className="font-mono font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              Elements
            </h3>
            
            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Search elements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg font-mono text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Type Filter */}
            <div className="flex gap-2">
              {['ALL', 'SEGMENT', 'SUPPORT', 'STATION'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type as any)}
                  className={`px-3 py-1 rounded-md text-xs font-mono font-medium transition-colors ${
                    filterType === type
                      ? 'bg-blue-600/80 text-white border border-blue-500/50'
                      : 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-600/50'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Elements List */}
          <div className="flex-1 overflow-y-auto dark-scrollbar bg-slate-50 dark:bg-transparent">
            {filteredElements.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm font-mono">
                No elements found
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredElements.map((element) => {
                  const elementPhotos = photos.filter(p => p.elementId === element.id);
                  const isSelected = selectedElement?.id === element.id;
                  
                  return (
                    <button
                      key={element.id}
                      onClick={() => handleElementClick(element)}
                      className={`w-full p-3 rounded-lg text-left transition-colors ${
                        isSelected
                          ? 'bg-blue-100 dark:bg-blue-600/20 border border-blue-300 dark:border-blue-500/50'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <span className={`text-[10px] font-mono font-medium uppercase px-2 py-0.5 rounded border ${
                          element.type === 'segment'
                            ? 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30'
                            : element.type === 'support'
                            ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30'
                            : 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30'
                        }`}>
                          {element.type}
                        </span>
                      </div>
                      <div className="font-mono font-medium text-slate-700 dark:text-slate-200 text-sm mb-1">
                        {element.code || `Element ${element.id.substring(0, 8)}`}
                      </div>
                      <div className="flex items-center gap-3 text-xs font-mono text-slate-500">
                        <span className="flex items-center gap-1">
                          <Camera className="w-3 h-3" />
                          {elementPhotos.length}
                        </span>
                        {/* TODO: Add anomaly count when implemented */}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      {/* Map Container */}
      <div className="flex-1 min-h-0 relative">
        {/* Show loading while data is being fetched */}
        {isDataLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Loading map data...</p>
            </div>
          </div>
        )}
        
        {/* Only render map when we have bounds OR data finished loading (even if no coordinates) */}
        {(!isDataLoading || bounds) && (
          <MapContainer
            key={bounds ? `map-${JSON.stringify(bounds)}-${mapTheme}` : `map-default-${mapTheme}`}
            center={bounds ? undefined : [45.0, 10.0]}
            zoom={bounds ? undefined : 6}
            bounds={bounds || undefined}
            boundsOptions={{ padding: [50, 50] }}
            className="h-full w-full"
            style={{ background: mapTheme === 'dark' ? '#0f172a' : '#f8fafc' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url={mapTheme === 'dark' 
                ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                : "https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
              }
              maxZoom={20}
            />
            
            {/* Center map on highlighted photo */}
            <HighlightedPhotoHandler photos={photos} highlightedPhotoId={highlightedPhotoId} />
          
          {/* Trajectory */}
          {layers.trajectory && trajectory.length > 1 && (() => {
            const validPositions = trajectory.filter(p => p && p[0] != null && p[1] != null);
            console.log('Rendering trajectory:', validPositions.length, 'valid points, first:', validPositions[0]);
            if (validPositions.length < 2) return null;
            return (
            <>
              {/* Main trajectory line */}
              <Polyline
                positions={validPositions}
                color="#ef4444"
                weight={4}
                opacity={0.9}
              />
              
              {/* Direction markers - show every 20th point */}
              {trajectory.map((_, i) => {
                if (i === 0 || i % 20 !== 0 || i >= trajectory.length - 1) return null;
                
                const current = trajectory[i];
                const next = trajectory[i + 1];
                
                // Skip if coordinates are invalid
                if (!current || !next || current[0] == null || current[1] == null || next[0] == null || next[1] == null) return null;
                
                // Calculate direction angle
                const dx = next[1] - current[1]; // longitude
                const dy = next[0] - current[0]; // latitude
                const angle = Math.atan2(dx, dy) * (180 / Math.PI);
                
                return (
                  <CircleMarker
                    key={`traj-dir-${i}`}
                    center={current}
                    radius={4}
                    fillColor="#dc2626"
                    color="#ffffff"
                    weight={1}
                    fillOpacity={1}
                  >
                    <Popup>
                      <div className="text-xs">
                        <strong>Trajectory Point {i}</strong>
                        <br />
                        Direction: {angle.toFixed(0)}°
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </>
          );
          })()}
          
          {/* Segments from server */}
          {layers.segments && elements.filter(el => el.type === 'segment').map((segment) => {
            if (!segment.geometry?.coordinates || segment.geometry.coordinates.length < 2) return null;
            // GeoJSON format is [lng, lat], Leaflet expects [lat, lng]
            // Filter out invalid coordinates
            const positions = segment.geometry.coordinates
              .filter((coord: number[] | null) => coord && coord[0] != null && coord[1] != null)
              .map((coord: number[]) => [coord[1], coord[0]] as [number, number]);
            if (positions.length < 2) return null;
            const isSelected = selectedElement?.id === segment.id;
            return (
              <Polyline
                key={segment.id}
                positions={positions}
                color={isSelected ? "#2563eb" : "#22c55e"}
                weight={isSelected ? 5 : 3}
                opacity={isSelected ? 1 : 0.8}
                dashArray="5, 10"
                eventHandlers={{
                  click: () => handleElementClick(segment),
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <strong>Segment: {segment.code}</strong>
                    <br />
                    <button
                      onClick={() => handleElementClick(segment)}
                      className="text-blue-600 hover:underline text-xs mt-1"
                    >
                      View Details
                    </button>
                  </div>
                </Popup>
              </Polyline>
            );
          })}
          
          {/* Supports from server */}
          {layers.supports && elements.filter(el => el.type === 'support').map((support) => {
            if (!support.lat || !support.lng) return null;
            const isSelected = selectedElement?.id === support.id;
            return (
              <CircleMarker
                key={support.id}
                center={[support.lat, support.lng]}
                radius={isSelected ? 10 : 6}
                fillColor={isSelected ? "#2563eb" : "#f59e0b"}
                color={isSelected ? "#1d4ed8" : "#d97706"}
                weight={2}
                fillOpacity={0.8}
                eventHandlers={{
                  click: () => handleElementClick(support),
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <strong>Support {support.elementId}</strong>
                    <br />
                    {support.code}
                    <br />
                    <button
                      onClick={() => handleElementClick(support)}
                      className="text-blue-600 hover:underline text-xs mt-1"
                    >
                      View Details
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
          
          {/* Stations from server */}
          {layers.stations && elements.filter(el => el.type === 'station').map((station) => {
            if (!station.lat || !station.lng) return null;
            const isSelected = selectedElement?.id === station.id;
            return (
              <CircleMarker
                key={station.id}
                center={[station.lat, station.lng]}
                radius={isSelected ? 12 : 8}
                fillColor={isSelected ? "#2563eb" : "#3b82f6"}
                color={isSelected ? "#1e3a8a" : "#1d4ed8"}
                weight={2}
                fillOpacity={0.8}
                eventHandlers={{
                  click: () => handleElementClick(station),
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <strong>Station {station.elementId}</strong>
                    <br />
                    {station.code}
                    <br />
                    <button
                      onClick={() => handleElementClick(station)}
                      className="text-blue-600 hover:underline text-xs mt-1"
                    >
                      View Details
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
          
          {/* Photos - rendered last to be on top */}
          {layers.photos && photos
            .filter(p => p.lat && p.lng)
            .map((photo) => {
              const isHighlighted = highlightedPhotoId === photo.id;
              return (
                <CircleMarker
                  key={photo.id}
                  center={[photo.lat, photo.lng]}
                  radius={isHighlighted ? 12 : 6}
                  fillColor={isHighlighted ? '#ef4444' : (photo.camera === 'FRONTE' ? '#3b82f6' : '#f59e0b')}
                  color={isHighlighted ? '#fef2f2' : '#ffffff'}
                  weight={isHighlighted ? 4 : 2}
                  fillOpacity={isHighlighted ? 1 : 0.8}
                  pathOptions={{ className: `photo-marker ${isHighlighted ? 'photo-highlighted' : ''}` }}
                >
                  <Popup className="photo-popup" minWidth={200} maxWidth={250}>
                    <div className="p-0">
                      {/* Photo thumbnail - clickable */}
                      <div 
                        className="relative cursor-pointer group"
                        onClick={() => handlePhotoClick(photo.id)}
                      >
                        {(photo.thumbnailUrl || photo.url) ? (
                          <img 
                            src={photo.thumbnailUrl || photo.url} 
                            alt={photo.filename}
                            className="w-full h-32 object-cover rounded-t-lg"
                          />
                        ) : (
                          <div className="w-full h-32 bg-gray-200 flex items-center justify-center rounded-t-lg">
                            <Camera className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-t-lg">
                          <div className="bg-white rounded-full p-2">
                            <Eye className="w-5 h-5 text-gray-800" />
                          </div>
                        </div>
                        {/* Camera badge */}
                        <span className={`absolute top-2 left-2 px-2 py-0.5 text-xs font-medium rounded ${
                          photo.camera === 'FRONTE' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-amber-500 text-white'
                        }`}>
                          {photo.camera || 'Photo'}
                        </span>
                      </div>
                      
                      {/* Info section */}
                      <div className="px-2 py-1.5">
                        <p className="text-xs text-gray-500 truncate" title={photo.filename}>
                          {photo.filename}
                        </p>
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        )}

        {/* Map Theme Toggle */}
        <div className="absolute top-4 right-4 z-[1000]">
          <button
            onClick={() => setMapTheme(t => t === 'light' ? 'dark' : 'light')}
            className={`
              p-2 rounded-lg shadow-lg border transition-colors
              ${mapTheme === 'dark' 
                ? 'bg-slate-800/90 border-slate-700/50 text-amber-400 hover:bg-slate-700' 
                : 'bg-white border-gray-200 text-slate-600 hover:bg-gray-50'
              }
            `}
            title={mapTheme === 'dark' ? 'Switch to light map' : 'Switch to dark map'}
          >
            {mapTheme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>

        {/* Compact Layer Control Overlay */}
        <div className={`absolute bottom-4 left-4 rounded-lg shadow-lg p-3 z-[1000] ${
          mapTheme === 'dark' 
            ? 'bg-slate-800/90 border border-slate-700/50 backdrop-blur-sm' 
            : 'bg-white border border-gray-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Layers className={`w-4 h-4 ${mapTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`} />
            <h4 className={`text-sm font-mono font-semibold ${mapTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'}`}>Layers</h4>
          </div>
          <div className="space-y-1">
            {trajectory.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={layers.trajectory}
                  onChange={(e) => setLayers(l => ({ ...l, trajectory: e.target.checked }))}
                  className="w-3 h-3 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span className={mapTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'}>Trajectory ({trajectory.length})</span>
              </label>
            )}
            <label className="flex items-center gap-2 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={layers.stations}
                onChange={(e) => setLayers(l => ({ ...l, stations: e.target.checked }))}
                className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className={mapTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'}>Stations ({elements.filter(el => el.type === 'station').length})</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={layers.segments}
                onChange={(e) => setLayers(l => ({ ...l, segments: e.target.checked }))}
                className="w-3 h-3 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className={mapTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'}>Segments ({elements.filter(el => el.type === 'segment').length})</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={layers.supports}
                onChange={(e) => setLayers(l => ({ ...l, supports: e.target.checked }))}
                className="w-3 h-3 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <span className={mapTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'}>Supports ({elements.filter(el => el.type === 'support').length})</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={layers.photos}
                onChange={(e) => setLayers(l => ({ ...l, photos: e.target.checked }))}
                className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className={mapTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'}>Photos ({photos.filter(p => p.lat && p.lng).length})</span>
            </label>
          </div>
        </div>
      </div>

      {/* Element Detail Panel (Right) */}
      {selectedElement && (
        <ElementDetailPanel
          elementId={selectedElement.id}
          elementType={selectedElement.type.toUpperCase()}
          elementCode={selectedElement.code || `Element ${selectedElement.id.substring(0, 8)}`}
          coordinates={
            selectedElement.geometry?.type === 'Point'
              ? { lat: selectedElement.geometry.coordinates[1], lng: selectedElement.geometry.coordinates[0] }
              : selectedElement.geometry?.type === 'LineString'
              ? selectedElement.geometry.coordinates.map((c: number[]) => ({ lat: c[1], lng: c[0] }))
              : selectedElement.lat && selectedElement.lng
              ? { lat: selectedElement.lat, lng: selectedElement.lng }
              : undefined
          }
          photos={photos}
          onClose={() => setSelectedElement(null)}
          onPhotoClick={handlePhotoClick}
        />
      )}

      {/* PhotoViewer Modal (overlay) */}
      {currentPhoto && (
        <div className="fixed inset-0 z-[2000]">
          <PhotoViewer />
        </div>
      )}
    </div>
  );
}
